import { Component, Input, OnChanges } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { HoursByDayData } from '../../../interfaces/metrics';

// Incoming day codes ('mo'/'tu'/'we'/'th'/'fr') → weekday_short i18n dictionary keys.
const DAY_CODE_TO_KEY: Record<string, string> = { mo: 'mon', tu: 'tue', we: 'wed', th: 'thu', fr: 'fri' };

@Component({
  selector: 'bst-week-hours',
  templateUrl: './week-hours.component.html',
  styleUrls: ['./week-hours.component.scss'],
  standalone: false
})
export class WeekHoursComponent implements OnChanges {
  @Input() data!: HoursByDayData;
  // True only for the instance nested in the merged Hours tile, which sits beside a KPI
  // card that already states the registered and planned totals; drops this card's own
  // Total/Plan columns so those two numbers are not printed twice under one heading.
  @Input() compact = false;
  options: EChartsOption = {};

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    if (!this.data) { return; }
    this.options = {
      grid: { left: 8, right: 8, top: 8, bottom: 22 },
      xAxis: {
        type: 'category',
        data: this.data.days.map(d => this.translate.instant('weekday_short.' + (DAY_CODE_TO_KEY[d.day] || d.day))),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontFamily: 'inherit', fontWeight: 600 }
      },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'bar',
          stack: 'h',
          barCategoryGap: '38%',
          name: 'billable',
          data: this.data.days.map(d => d.billable),
          itemStyle: { color: '#1863DC', borderRadius: [0, 0, 2, 2] },
          // Proportional, not a fixed pixel width: this chart now lives inside the merged
          // Hours tile, whose width changes with the user's arrangement. barMaxWidth keeps
          // bars from ballooning in a wide tile; barCategoryGap sets the proportion.
          barMaxWidth: 44
        },
        {
          type: 'bar',
          stack: 'h',
          name: 'nonbillable',
          data: this.data.days.map(d => d.nonBillable),
          itemStyle: { color: '#CBD6E2', borderRadius: [2, 2, 0, 0] }
        }
      ],
      animation: false
    };
  }
}
